import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { GeofencingDialogResult, IGeofencingLayer } from "../../compound-components/model";
import { GeoJSONDatabaseService } from "../../viewer-components/services/internal-services/geojson-database.service";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";
import { Viewerlayer } from "../../viewer-components/model/viewerlayer";

/**
 * Request to create, update, or remove geofencing layer
 */
export interface GeofencingLayerRequest {
  /** Unique request identifier */
  id: string | null;
  /** Type of operation */
  operation: "create" | "update" | "remove" | "toggle-visibility";
  /** Geofencing data from dialog */
  data?: GeofencingDialogResult;
  /** Context information */
  context?: {
    source: "settings-dialog" | "header-toggle";
    timestamp: number;
  };
}

/**
 * Result of geofencing layer operation
 */
export interface GeofencingLayerResult {
  /** Request ID this result corresponds to */
  requestId: string | null;
  /** Whether operation was successful */
  success: boolean;
  /** Current layer state after operation */
  state: GeofencingLayerState;
  /** Error message if operation failed */
  error?: string;
}

export interface GeofencingLayerState {
  layerExists: boolean;
  layerVisible: boolean;
  hasGeometry: boolean;
  geometryType: "polygon" | "circle" | null;
  displayWkt?: string;
}

/**
 * Service to handle geofencing layer workflow communication between components
 * Follows the same pattern as MapSelectionService for consistency
 */
@Injectable({
  providedIn: "root",
})
export class GeofencingService {
  private readonly geoJSONDatabaseService = inject(GeoJSONDatabaseService);

  // Request stream - components send requests to map container

  private geofencingLayerRequestSubject = new BehaviorSubject<GeofencingLayerRequest | null>(null);

  // Result stream - map container sends results back to components
  private geofencingLayerResultSubject = new BehaviorSubject<GeofencingLayerResult | null>(null);

  // State stream - current geofencing state for reactive UI updates
  private geofencingStateSubject = new BehaviorSubject<GeofencingLayerState>({
    layerExists: false,
    layerVisible: false,
    hasGeometry: false,
    geometryType: null,
  });

  // Public observables

  /** Observable for geofencing layer requests (consumed by map container) */
  geofencingLayerRequest$: Observable<GeofencingLayerRequest | null> =
    this.geofencingLayerRequestSubject.asObservable();

  /** Observable for geofencing layer results (consumed by requesting components) */
  geofencingLayerResult$: Observable<GeofencingLayerResult | null> = this.geofencingLayerResultSubject.asObservable();

  /** Observable for current geofencing state (consumed by header and other UI components) */
  geofencingState$: Observable<GeofencingLayerState> = this.geofencingStateSubject.asObservable();

  // Request methods (called by UI components)

  /**
   * Request creation or update of geofencing layer
   * @param geofencingData Data from geofencing dialog
   */
  requestCreateOrUpdateLayer(geofencingData: GeofencingDialogResult): void {
    const request: GeofencingLayerRequest = {
      id: `geofencing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      operation: "create", // Will be updated to existing layer if needed
      data: geofencingData,
      context: {
        source: "settings-dialog",
        timestamp: Date.now(),
      },
    };

    this.geofencingLayerRequestSubject.next(request);
  }

  /**
   * Request toggle of geofencing layer visibility
   */
  requestToggleVisibility(): void {
    const request: GeofencingLayerRequest = {
      id: `toggle-${Date.now()}`,
      operation: "toggle-visibility",
      context: {
        source: "header-toggle",
        timestamp: Date.now(),
      },
    };

    this.geofencingLayerRequestSubject.next(request);
  }

  /**
   * Request removal of geofencing layer
   */
  requestRemoveLayer(): void {
    const request: GeofencingLayerRequest = {
      id: `remove-${Date.now()}`,
      operation: "remove",
      context: {
        source: "settings-dialog",
        timestamp: Date.now(),
      },
    };

    this.geofencingLayerRequestSubject.next(request);
  }

  // Result methods (called by map container)

  /**
   * Send result of geofencing layer operation back to requesting components
   * @param result Operation result
   */
  sendLayerOperationResult(result: GeofencingLayerResult): void {
    this.geofencingLayerResultSubject.next(result);

    // Update state stream
    this.geofencingStateSubject.next(result.state);
  }

  /**
   * Update current geofencing state (for reactive UI updates)
   * @param state Current state
   */
  updateGeofencingState(state: GeofencingLayerState): void {
    this.geofencingStateSubject.next(state);
  }

  // Utility methods

  /**
   * Get current geofencing state synchronously
   */
  getCurrentState(): GeofencingLayerState {
    return this.geofencingStateSubject.value;
  }

  /**
   * Error handling utility
   */
  showError(error: string): void {
    console.error("[GeofencingService] Error:", error);
    // TODO: Consider implementing user notification for critical errors
  }

  /**
   * Returns the user's map layers mapped to IGeofencingLayer (id, name, wkt).
   * Layers without extractable geometry are filtered out.
   */
  getAvailableLayers$(): Observable<IGeofencingLayer[]> {
    return this.geoJSONDatabaseService.getLayers().pipe(
      take(1),
      catchError(() => of([] as Viewerlayer[])),
      map((layers) =>
        layers
          .map((l) => {
            const wkt = GeoService.geoJSONFeaturesToWkt(l.source?.features?.features ?? []);
            return wkt ? { id: String(l.id), name: l.name, wkt } : null;
          })
          .filter((l): l is IGeofencingLayer => l !== null),
      ),
    );
  }
}
